import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { IMG } from "@/data/site";
import { Marquee } from "./Reveal";
import { Spark } from "./Icons";

const Stamp = ({ text }) => (
  <div className="relative w-36 h-36 sm:w-44 sm:h-44 shrink-0" data-testid="hero-stamp">
    <svg viewBox="0 0 200 200" className="absolute inset-0 spin-slow" aria-hidden="true">
      <defs>
        <path id="stampCircle" d="M100,100 m-80,0 a80,80 0 1,1 160,0 a80,80 0 1,1 -160,0" />
      </defs>
      <circle cx="100" cy="100" r="97" fill="#0F0F10" stroke="#F4EFE6" strokeWidth="2" />
      <circle cx="100" cy="100" r="64" fill="none" stroke="#F4EFE6" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="3 4" />
      <text fill="#F4EFE6" fontFamily="IBM Plex Mono, monospace" fontSize="13" fontWeight="600" letterSpacing="2">
        <textPath href="#stampCircle" textLength="495" lengthAdjust="spacing">
          {text}
        </textPath>
      </text>
    </svg>
    <div className="absolute inset-[29%] rounded-full bg-hammer-red border-2 border-bone grid place-items-center rotate-[-8deg]">
      <span className="font-display text-bone text-center leading-[0.8] text-[26px] sm:text-[32px] pt-1">
        EST
        <br />
        2011
      </span>
    </div>
  </div>
);

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [isMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", isMobile ? "8%" : "14%"]);
  const marqueeY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) setLoaded(true);
  }, []);

  const sep = <Spark className="mx-[0.18em] w-[0.26em] h-[0.26em] text-hammer-red" />;

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] bg-ink text-bone overflow-hidden flex flex-col"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          ref={imgRef}
          src={IMG.heroHd}
          alt="Sala pesi Hammer Gym Lugano con il murale di Arnold"
          onLoad={() => setLoaded(true)}
          data-testid="hero-image"
          className={`w-full h-full object-cover object-[62%_center] photo-hero transition-opacity duration-700 ease-out ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
          fetchpriority="high"
          decoding="async"
        />
      </motion.div>
      <div className="tint-red" />
      <div className="hero-shade" />
      <div className="vignette" />

      {/* meta row */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28">
        <div className="flex flex-wrap gap-x-6 gap-y-1 justify-between font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-bone/70 border-t border-bone/25 pt-3">
          <span data-testid="hero-eyebrow">N.001 / {t.hero.eyebrow}</span>
          <span className="hidden md:inline">46.03°N — 8.96°E</span>
          <span>Old School Bodybuilding Gym</span>
        </div>
      </div>

      {/* giant overprint marquee */}
      <motion.div style={{ y: marqueeY }} className="relative z-10 mt-6 sm:mt-8 -rotate-[2deg] select-none" aria-hidden="true">
        <Marquee
          items={["HAMMER GYM", "HAMMER GYM"]}
          speed={28}
          separator={sep}
          className="hero-giant text-bone"
        />
        <Marquee
          items={["OLD SCHOOL", "LUGANO", "NO EXCUSES", "EST. 2011"]}
          speed={38}
          reverse
          separator={<span className="mx-[0.35em] text-hammer-red">/</span>}
          className="font-display text-outline text-[clamp(3.5rem,10vw,9rem)] leading-[0.9] -mt-[0.5vw] opacity-80"
        />
      </motion.div>

      {/* content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-auto pb-10 sm:pb-14 pt-8">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <h1 data-testid="hero-title" className="font-display text-[3.4rem] sm:text-7xl lg:text-[6.2rem] leading-[0.86]">
              <span className="block">{t.hero.title[0]}</span>
              <span className="block">
                <span className="text-hammer-red">{t.hero.title[1]}</span> {t.hero.title[2]}
              </span>
            </h1>
            <p data-testid="hero-subtitle" className="mt-5 max-w-[54ch] text-[15px] sm:text-base leading-relaxed text-bone/85">
              {t.hero.sub}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-4">
              <a href="#prova" data-testid="hero-free-trial-button" className="btn-brutal on-dark btn-red">
                {t.hero.ctaTrial}
                <ArrowUpRight className="w-5 h-5" />
              </a>
              <a href="#prezzi" data-testid="hero-prices-button" className="btn-brutal on-dark btn-bone">
                {t.hero.ctaPrices}
              </a>
            </div>
          </motion.div>

          <div className="lg:col-span-5 flex items-end justify-between lg:justify-end gap-6">
            <span className="font-hand text-hammer-red text-3xl sm:text-4xl -rotate-6 mb-10 lg:mb-16" data-testid="hero-scribble">
              {t.hero.scribble}
            </span>
            <Stamp text={t.hero.stamp} />
          </div>
        </div>

        <a
          href="#info"
          data-testid="hero-scroll-indicator"
          className="hidden sm:flex mt-10 items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-bone/60 hover:text-bone w-max"
        >
          <ArrowDown className="w-4 h-4 animate-bounce" /> {t.hero.scroll}
        </a>
      </div>
    </section>
  );
};
